import { resolveActiveProductFlags } from '../../components/ProductFlags/productFlagsResolve';
import type { Result } from '../../types/result';
import type { ResultCardPresentation } from '../../types/result-card-presentation';
import { paragraphToSentencesArray } from '../programDescriptions';

const DEFAULT_CTA_LABEL = 'Explore Program';
const DEFAULT_LIST_HEADING = 'Why Consider This Program';
const DEFAULT_TRUSTED_PARTNER_TEXT =
  'Sponsored school. We may receive compensation from partner schools.';

const buildSchoolLogoUrl = (result: Result): string | undefined => {
  if (result.imageURL) {
    return result.imageURL;
  }
  if (result.schoolSlug) {
    return `https://simple-storage-server.highereducation.com/${result.schoolSlug}.png?w=130&h=80`;
  }
  return undefined;
};

const buildSchoolDetails = (result: Result): string | undefined => {
  const line = [result.city, result.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  return line || undefined;
};

const buildTags = (result: Result): string[] =>
  resolveActiveProductFlags({
    levelOfInstitution: result.levelOfInstitution,
    accreditator: result.accreditator,
    roiPercentileRank: result.roiPercentileRank,
    percentOnlineEnrollment: result.percentOnlineEnrollment ?? undefined,
    isOnline: result.isOnline,
    isPublic: result.isPublic,
    forProfit: result.forProfit,
    notForProfit: result.notForProfit,
  }).map(({ flag }) => flag.value);

const buildListItems = (snippet: string | undefined): string[] => {
  if (!snippet?.trim()) {
    return [];
  }

  return paragraphToSentencesArray(snippet)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
};

/**
 * Maps a domain {@link Result} into the additive card presentation view model
 * used by RP DSL configs. Mirrors the display derivations in
 * `components/Card/Default.tsx` without runtime-only state (compare, submitted).
 */
export const toResultCardPresentation = (
  result: Result,
): ResultCardPresentation => {
  const listItems = buildListItems(result.snippet);
  const tags = buildTags(result);

  return {
    schoolLogo: buildSchoolLogoUrl(result),
    schoolName: result.schoolName,
    schoolDetails: buildSchoolDetails(result),
    degreeTitle: result.programName,
    tags: tags.length > 0 ? tags : undefined,
    description: result.snippet,
    listHeading: listItems.length > 0 ? DEFAULT_LIST_HEADING : undefined,
    listItems: listItems.length > 0 ? listItems : undefined,
    ctaLabel: result.applicationCtaLabel || DEFAULT_CTA_LABEL,
    trustedPartnerText: DEFAULT_TRUSTED_PARTNER_TEXT,
  };
};

export const attachResultCardPresentation = (
  result: Result,
): Result & {
  card: ResultCardPresentation;
} => ({
  ...result,
  card: toResultCardPresentation(result),
});
